/**
 * "The record on this screen came from TravelBooster, so it cannot be changed."
 *
 * Imported records are read-only — the database refuses to update them (see
 * data/scripts/sql/split_app_db_with_fdw_views.sql). Without something telling
 * the UI, a user fills in a form, presses Save and gets a 403. This carries that
 * one fact down the tree so every button can disable itself.
 *
 * There are TWO shapes of "locked" in this app and they need different tools:
 *
 *   1. A screen scoped to ONE record — a travel file page, a service form, a
 *      tour form. Wrap it in <ImportedRecordScope id={...}> and any component
 *      inside asks useImportedLock().
 *
 *   2. A grid whose toolbar acts on the SELECTED rows — supplier reconciliation,
 *      payment book. There is no single record in scope; the answer changes with
 *      the selection. Those use useHasImportedSelection(rows) instead.
 *
 * Mixing the two up is the easy mistake: a provider around a grid would claim
 * the whole page is locked the moment one imported row exists.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { isImportedId, IMPORTED_RECORD_TOOLTIP } from '../api/services/constants/importedRecords';
import { Context as AuthContext } from '../api/auth/context';
import vouchersApi from '../api/vouchers/api';

/**
 * `app_config` key: has TravelBooster been retired?
 *
 * See {@link ImportedRecordRetirementProvider} for what it does and does not do.
 */
export const TB_RETIRED_CONFIG_KEY = 'travelbooster_retired';

type ImportedRecordScopeValue = {
    /** Is the record in scope imported, and therefore read-only? */
    isImported: boolean;
};

const DEFAULT: ImportedRecordScopeValue = { isImported: false };

const ImportedRecordContext = createContext<ImportedRecordScopeValue>(DEFAULT);

type ScopeProps = {
    /**
     * The record's id. `undefined` in create mode, which is never imported —
     * a record being created is always native.
     */
    id?: string | number | null;
    children: React.ReactNode;
};

/**
 * Mark everything inside as belonging to one record.
 *
 * Nesting only ever LOCKS, never unlocks: `parent.isImported || isImportedId(id)`.
 * That one line is what makes every nested case come out right —
 *
 *   - a service form inside an imported travel file, in edit mode → locked by
 *     its own id
 *   - the same form in CREATE mode, where it has no id yet → still locked,
 *     inherited from the travel file. Which is the case that matters most: the
 *     database does NOT refuse a new child row hung off an imported parent, so
 *     the UI is the only thing preventing a service that will never exist in
 *     TravelBooster.
 *   - a new document issued from a native travel file → open, as it should be.
 *
 * Because it can only tighten, it is safe to mount widely.
 */
export const ImportedRecordScope: React.FC<ScopeProps> = ({ id, children }) => {
    const parent = useContext(ImportedRecordContext);
    const value = useMemo(
        () => ({ isImported: parent.isImported || isImportedId(id) }),
        [parent.isImported, id]
    );
    return <ImportedRecordContext.Provider value={value}>{children}</ImportedRecordContext.Provider>;
};

/**
 * "TravelBooster has been retired, so imported records are ordinary records."
 *
 * The end state of this project is that Pegasus works only in Taytan. On that
 * day the imported data stops being a replica of a live system and becomes
 * ordinary history, and there is no longer any reason to lock it.
 *
 * THIS FLAG IS THE LAST STEP OF THAT MIGRATION, NOT THE MIGRATION. The lock has
 * two layers and this is only the outer one:
 *
 *   - the UI (this module), which the flag switches off
 *   - the database — tb_view_write() refuses every UPDATE of an imported id, and
 *     the FDW mapping holds SELECT only — which the flag cannot touch
 *
 * Turning it on before the data has been migrated out of the import database
 * therefore makes things WORSE, not better: the honest "you cannot change this"
 * disappears and every save fails with a 403 instead. The procedure that has to
 * run first is data/scripts/sql/retire_travelbooster.sql.
 *
 * Note what the flag does NOT change: an id in the imported band still means
 * "this record originally came from TravelBooster", forever. That provenance
 * stays useful after retirement — the general-ledger export filters on it, and
 * it is the only record of where a row came from. The flag separates provenance
 * from writability; it does not erase provenance.
 *
 * Fetched once here rather than per component: useImportedLock() is called on
 * most screens in the app, and a per-mount fetch would be one HTTP request per
 * component. Kept in its own context, separate from the record scope, so a
 * nested <ImportedRecordScope> never has to thread it through and cannot hold a
 * stale copy.
 */
const RetirementContext = createContext<boolean>(false);

export const ImportedRecordRetirementProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const [retired, setRetired] = useState(false);
    // The route sits behind authMiddleWare. Mounted at the app root, this
    // provider exists on the login page too, so fetching on mount 401'd every
    // logged-out load — and, having fetched once, never asked again after
    // login. Keyed to the user instead: no user, no request; a new login
    // re-reads the flag.
    const userId = useContext(AuthContext)?.currUser?._id;

    useEffect(() => {
        if (!userId) {
            setRetired(false);
            return;
        }
        let alive = true;
        vouchersApi
            .getAppConfig(TB_RETIRED_CONFIG_KEY)
            // Any failure leaves it false, which is the safe direction: the lock
            // stays on. A network blip must never quietly unlock the app.
            .then((result: any) => alive && setRetired(result?.value === 'true'))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [userId]);

    return <RetirementContext.Provider value={retired}>{children}</RetirementContext.Provider>;
};


/**
 * Has TravelBooster been retired? The raw flag, with no record scope.
 *
 * For the handful of places that need the flag itself rather than "is THIS
 * record locked" — work that is pointless while the imported half is read-only
 * and becomes correct again the moment the data is folded in.
 */
export function useTravelBoosterRetired(): boolean {
    return useContext(RetirementContext);
}

/**
 * Is the record in scope read-only, and what should the tooltip say?
 *
 *     const { locked, reason } = useImportedLock();
 *     <Tooltip title={reason}><span>
 *         <Button disabled={locked || !canEdit}>Save</Button>
 *     </span></Tooltip>
 *
 * `reason` is '' when nothing is locked, which MUI renders as no tooltip at all,
 * so the same markup works in both states.
 */
export function useImportedLock(): { locked: boolean; reason: string } {
    const { isImported } = useContext(ImportedRecordContext);
    const retired = useContext(RetirementContext);
    const locked = isImported && !retired;
    return useMemo(
        () => ({ locked, reason: locked ? IMPORTED_RECORD_TOOLTIP : '' }),
        [locked]
    );
}

/**
 * The grid-toolbar counterpart: does the current SELECTION contain an imported
 * record?
 *
 * Toolbar actions — Reconcile, Receipt, Remittance — write back to the rows that
 * were selected, so one imported row in the selection is enough to fail the
 * whole action partway through.
 */
export function useHasImportedSelection(
    rows: Array<{ _id?: string | number | null } | null | undefined> | null | undefined
): { locked: boolean; reason: string } {
    const retired = useContext(RetirementContext);
    const locked = !retired && !!rows?.some((row) => isImportedId(row?._id));
    return useMemo(
        () => ({ locked, reason: locked ? IMPORTED_RECORD_TOOLTIP : '' }),
        [locked]
    );
}
